import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'

import { CommandPalette } from '@/components/layout/CommandPalette'
import { Sidebar } from '@/components/layout/Sidebar'
import { Topbar } from '@/components/layout/Topbar'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { useWhitelistStore } from '@/stores/whitelist'

/** How often the shell re-reads the badge counters and bot health. */
const POLL_INTERVAL_MS = 60_000

export function AppShell() {
  const summary = useDashboardStore((state) => state.summary.data)
  const load = useDashboardStore((state) => state.load)
  const whitelistStats = useWhitelistStore((state) => state.stats.data)
  const loadWhitelistStats = useWhitelistStore((state) => state.loadStats)
  // The badge is only fetched for staff who can act on it — everyone else would
  // just be making a request that returns 403.
  const canSeeWhitelist = useAuthStore((state) =>
    state.user?.is_superuser ? true : state.permissions.has('whitelist.view'),
  )

  useEffect(() => {
    void load()
    if (canSeeWhitelist) void loadWhitelistStats()

    // Paused while the tab is hidden: a console left open overnight should not
    // keep polling, and it refetches immediately when the operator returns.
    const tick = () => {
      if (document.visibilityState !== 'visible') return
      void load()
      if (canSeeWhitelist) void loadWhitelistStats()
    }
    const timer = setInterval(tick, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', tick)

    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [load, loadWhitelistStats, canSeeWhitelist])

  const whitelistOpen = whitelistStats ? whitelistStats.pending + whitelistStats.check_submitted : 0

  const badges = {
    openTickets: summary?.stats.find((stat) => stat.key === 'open_tickets')?.value ?? 0,
    pendingModeration: summary?.stats.find((stat) => stat.key === 'moderation_queue')?.value ?? 0,
    whitelist: whitelistOpen,
  }

  return (
    <div className="bg-background flex h-dvh overflow-hidden">
      <Sidebar badges={badges} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar bot={summary?.bot ?? null} />
        {/* The gutter lives on the element that scrolls, so a page does not jump
            sideways when it grows long enough to need the scrollbar. */}
        <main className="scrollbar-thin flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="mx-auto w-full max-w-[1400px] px-5 py-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette />
    </div>
  )
}
